import { useCallback, useEffect, useRef, useState } from "react"
import {
  customSoundId,
  DEFAULT_SOUND_ID,
  isCustomSoundId,
  playSoundById,
  preloadSoundUrl,
  SOUND_PRESETS,
  unlockAudio,
} from "../../utils/chat/soundPresets"
import { useCompanySounds } from "./useCompanySounds"

export const SOUND_CATEGORIES = {
  USERS: "users",
  SYSTEM: "system",
}

// Звук по умолчанию, пока компания ничего не выбрала
export const DEFAULT_COMPANY_SOUNDS = {
  [SOUND_CATEGORIES.USERS]: "soft",
  [SOUND_CATEGORIES.SYSTEM]: "bell",
}

export const resolveSoundId = (categoryKey, userSound, companySettings) =>
  userSound ?? companySettings?.[categoryKey] ?? DEFAULT_COMPANY_SOUNDS[categoryKey] ?? DEFAULT_SOUND_ID

export const DEFAULT_SOUND_SETTINGS = {
  enabled: false,
  volume: 0.6,
  // не играть, если сообщение пришло в чат, который сейчас открыт на активной вкладке
  muteActiveChat: true,
  categories: {
    // sound: null — играет звук компании; строка — личное переопределение (на будущее)
    [SOUND_CATEGORIES.USERS]: { enabled: true, sound: null },
    [SOUND_CATEGORIES.SYSTEM]: { enabled: false, sound: null },
  },
  departments: {
    mode: "all", // "all" | "selected"
    ids: [],
    // сообщения без отдела (и все сообщения в системах, где отделов нет вообще)
    includeWithout: true,
  },
}

const MIN_INTERVAL_MS = 1200
const SEEN_LIMIT = 200

// v2: в первой версии здесь хранился личный выбор звука, теперь звук выбирает компания
const storageKey = (userId) => `chat_sound_settings_v2_${userId ?? "anon"}`

// Допустимы базовые пресеты и звуки компании ("custom:<id>"), иначе — null (звук компании).
// Существует ли звук компании на самом деле, проверяется позже — когда загрузится библиотека.
const normalizeSound = (id) => (SOUND_PRESETS[id] || isCustomSoundId(id) ? id : null)

const normalizeSettings = (raw) => {
  const d = DEFAULT_SOUND_SETTINGS
  const r = raw && typeof raw === "object" ? raw : {}
  const rc = r.categories ?? {}
  const rd = r.departments ?? {}

  const category = (key) => ({
    enabled: typeof rc[key]?.enabled === "boolean" ? rc[key].enabled : d.categories[key].enabled,
    sound: normalizeSound(rc[key]?.sound),
  })

  return {
    enabled: typeof r.enabled === "boolean" ? r.enabled : d.enabled,
    volume:
      typeof r.volume === "number" && r.volume >= 0 && r.volume <= 1 ? r.volume : d.volume,
    muteActiveChat:
      typeof r.muteActiveChat === "boolean" ? r.muteActiveChat : d.muteActiveChat,
    categories: {
      [SOUND_CATEGORIES.USERS]: category(SOUND_CATEGORIES.USERS),
      [SOUND_CATEGORIES.SYSTEM]: category(SOUND_CATEGORIES.SYSTEM),
    },
    departments: {
      mode: rd.mode === "selected" ? "selected" : "all",
      ids: Array.isArray(rd.ids) ? rd.ids.map(Number).filter((n) => n > 0) : [],
      includeWithout:
        typeof rd.includeWithout === "boolean" ? rd.includeWithout : d.departments.includeWithout,
    },
  }
}

const loadSettings = (userId) => {
  try {
    const raw = localStorage.getItem(storageKey(userId))
    return normalizeSettings(raw ? JSON.parse(raw) : null)
  } catch (_) {
    return normalizeSettings(null)
  }
}

const saveSettings = (userId, settings) => {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(settings))
  } catch (_) {}
}

// Системное = нет автора (user_id 0/null), это не клиент из бота и не сообщение партнёра.
// Например, уведомления об изменении времени рейса (message_context = flight_time_changed).
const classifyNotify = (n) => {
  const isSystem =
    !n.user_id && !n.from_client && !(Number(n.provider_id) > 0) && n.is_provider_notify !== true
  return isSystem ? SOUND_CATEGORIES.SYSTEM : SOUND_CATEGORIES.USERS
}

export const useChatSound = ({ currentUserId, currentCompanyId }) => {
  const [settings, setSettings] = useState(() => loadSettings(currentUserId))

  // Библиотеку и выбранные звуки компании грузим только когда звук включён — так лишнего запроса
  // на каждой странице нет. Панель настроек сама подписывается на тот же кэш без skip.
  const library = useCompanySounds({ skip: !settings.enabled })

  const settingsRef = useRef(settings)
  settingsRef.current = settings

  const libraryRef = useRef(library)
  libraryRef.current = library

  const identityRef = useRef({ currentUserId, currentCompanyId })
  identityRef.current = { currentUserId, currentCompanyId }

  const seenRef = useRef(new Set())
  const lastPlayedRef = useRef(0)

  // смена пользователя — подгружаем его настройки
  const loadedForRef = useRef(currentUserId)
  useEffect(() => {
    if (loadedForRef.current === currentUserId) return
    loadedForRef.current = currentUserId
    setSettings(loadSettings(currentUserId))
  }, [currentUserId])

  useEffect(() => {
    saveSettings(loadedForRef.current, settings)
  }, [settings])

  // Заранее декодируем звуки, которые будут играть, чтобы первое уведомление не задерживалось
  useEffect(() => {
    if (!settings.enabled || !library.loaded) return

    Object.entries(settings.categories).forEach(([key, cfg]) => {
      if (!cfg.enabled) return
      const soundId = resolveSoundId(key, cfg.sound, library.settings)
      if (!isCustomSoundId(soundId)) return

      const sound = library.sounds.find((s) => customSoundId(s.id) === soundId)
      if (sound) preloadSoundUrl(sound.url)
    })
  }, [settings, library])

  // Личное переопределение указывало на удалённый звук компании — убираем его (снова играет звук компании)
  useEffect(() => {
    if (!library.loaded) return

    const existing = new Set(library.sounds.map((s) => customSoundId(s.id)))

    setSettings((prev) => {
      let changed = false
      const categories = { ...prev.categories }

      Object.keys(categories).forEach((key) => {
        const current = categories[key].sound
        if (isCustomSoundId(current) && !existing.has(current)) {
          categories[key] = { ...categories[key], sound: null }
          changed = true
        }
      })

      return changed ? { ...prev, categories } : prev
    })
  }, [library.loaded, library.sounds])

  // разблокировка звука первым действием пользователя
  useEffect(() => {
    const unlock = () => {
      unlockAudio()
      window.removeEventListener("pointerdown", unlock)
      window.removeEventListener("keydown", unlock)
    }
    window.addEventListener("pointerdown", unlock)
    window.addEventListener("keydown", unlock)
    return () => {
      window.removeEventListener("pointerdown", unlock)
      window.removeEventListener("keydown", unlock)
    }
  }, [])

  const updateSettings = useCallback((patch) => {
    setSettings((prev) => normalizeSettings(typeof patch === "function" ? patch(prev) : patch))
  }, [])

  // sounds — список звуков компании. Панель настроек передаёт его сама: пока звук выключен,
  // библиотека в этом хуке не грузится (skip), а прослушать свой звук всё равно нужно.
  const preview = useCallback((soundId, sounds) => {
    unlockAudio()
    playSoundById(
      soundId ?? DEFAULT_SOUND_ID,
      settingsRef.current.volume,
      sounds ?? libraryRef.current.sounds
    )
  }, [])

  /**
   * data — событие ActionCable { chat_type, chat_id, notify }.
   * Одно сообщение может прийти и по комнате, и по персональному потоку — дедуплицируем по id.
   */
  const notify = useCallback((data, { isActiveChat = false } = {}) => {
    const s = settingsRef.current
    if (!s.enabled) return

    const n = data?.notify
    if (!n || data.chat_type === "ai_bot") return

    const key = `${data.chat_type}_${n.id}`
    const seen = seenRef.current
    if (seen.has(key)) return
    seen.add(key)
    if (seen.size > SEEN_LIMIT) seen.delete(seen.values().next().value)

    const { currentUserId: uid, currentCompanyId: cid } = identityRef.current

    // свои сообщения и сообщения коллег из своей компании (как и в счётчике непрочитанных)
    if (n.user_id && Number(n.user_id) === Number(uid)) return
    if (n.user_company_id && cid && Number(n.user_company_id) === Number(cid)) return

    if (n.is_hidden === true || n.is_read === true) return

    const categoryKey = classifyNotify(n)
    const cfg = s.categories[categoryKey]
    if (!cfg?.enabled) return

    if (s.departments.mode === "selected") {
      const deptId = Number(n.departament_id) || 0
      if (deptId === 0) {
        if (!s.departments.includeWithout) return
      } else if (!s.departments.ids.includes(deptId)) {
        return
      }
    }

    if (
      s.muteActiveChat &&
      isActiveChat &&
      typeof document !== "undefined" &&
      document.visibilityState === "visible"
    ) {
      return
    }

    const now = Date.now()
    if (now - lastPlayedRef.current < MIN_INTERVAL_MS) return
    lastPlayedRef.current = now

    const lib = libraryRef.current
    playSoundById(resolveSoundId(categoryKey, cfg.sound, lib.settings), s.volume, lib.sounds)
  }, [])

  return { settings, updateSettings, notify, preview }
}

export default useChatSound
